import {useUpdateRefractoryCustomization, type Refractory} from '@domain/refractory-product-catalog'
import {useUpmPlantContext} from '@hconnect/upm'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {ColorPreview, LocalColorCell, ResetLocalColorCell} from '@widgets/color-picker'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {DeleteRefractoryCell} from '../components/DeleteRefractoryCell'
import {RefraCatalogDocumentsDropdown} from '../components/documents'
import {FEATURE_NAMESPACE} from '../consts'
import type {RefraCatalogData} from '../types'

interface useRefraCatalogDataProps {
  refractories: Refractory[] | undefined
}

export const useRefraCatalogData = ({refractories}: useRefraCatalogDataProps) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {upmId} = useUpmPlantContext()
  const {mutate: updateCustomization} = useUpdateRefractoryCustomization({
    onError: () => spawnErrorToast(t('error.updateRefractoryCustomization')),
    onSuccess: () => spawnSuccessToast(t('success.updateRefractoryCustomization'))
  })

  return useMemo<RefraCatalogData[]>(() => {
    if (!refractories) return []

    return [...refractories].map((refractory) => ({
      supplier: refractory.manufacturer.name,
      brandName: refractory.name,
      classificationGroup: refractory.classificationGroup.name,
      subcategories: refractory.classification.classification,
      tds: (
        <RefraCatalogDocumentsDropdown
          refractoryId={refractory.id}
          documents={refractory.documents}
        />
      ),
      bulkDensity: refractory.physicalData.BulkDensity?.[0].metric ?? '-',
      cyPostfix: `row-${refractory.name}`,
      color: <ColorPreview color={refractory.color} />,
      localColor: (
        <LocalColorCell
          globalColor={refractory.color}
          localColor={refractory.customization?.color ?? undefined}
          onColorChange={(color) =>
            updateCustomization({plantUpmId: upmId, refractoryId: refractory.id, color})
          }
          dataCy={`catalog-local-color-${refractory.id}`}
        />
      ),
      actions: (
        <ResetLocalColorCell
          globalColor={refractory.color}
          localColor={refractory.customization?.color ?? undefined}
          onColorReset={() =>
            updateCustomization({plantUpmId: upmId, refractoryId: refractory.id, color: null})
          }
          dataCy={`reset-local-color-${refractory.id}`}
        />
      ),
      delete: (
        <DeleteRefractoryCell
          refractoryId={refractory.id}
          refractoryName={refractory.name}
          dataCy={`delete-refractory-${refractory.id}`}
        />
      )
    }))
  }, [refractories, upmId, updateCustomization])
}
